import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { postComment, searchMentionable } from '@/notifications/api'
import {
  completeActivity,
  createTask,
  fetchActivities,
  logCall,
  mayCompleteActivity,
  mayLogActivity,
  nextStepOf,
  type Activity,
  type ActivityEntityType,
} from './activityApi'
import { feedItemOf } from './recordHelpers'
import { TimelineMore, useTimeline, type TimelineFeed } from './useTimeline'
import { Composer, Feed, NextStep, StateView, type ComposerMode, type FeedItem } from './record'

/**
 * Работа с записью, у которой есть лента взаимодействий (#79): лид и контакт.
 *
 * Та же связка, что на карточке сделки (DealRecordView): следующий шаг, композер и лента
 * одним потоком. Вынесено, чтобы лид и контакт не повторяли друг друга — и не разошлись
 * в том, кому приписывается звонок.
 */

export interface ActivityTarget {
  /** Тип активности и ленты (`lead`, `contact`). */
  type: ActivityEntityType
  /** Сущность каталога списков — у комментариев свой словарь (`sales.lead`). */
  entity: string
  /** Ресурс права на правку записи: звонок и задача — правка её истории. */
  resource: string
  recordId: string
}

/** Запросы ленты и активностей записи; ключи общие с инвалидацией после записи. */
export function useRecordActivity(target: ActivityTarget) {
  const timeline = useTimeline(target.type, target.recordId)
  const activities = useQuery({
    queryKey: ['activities', target.type, target.recordId],
    queryFn: ({ signal }) => fetchActivities(target.type, target.recordId, 100, signal),
    retry: false,
  })
  return { timeline, activities }
}

export function useRefreshActivity(target: ActivityTarget) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['timeline', target.type, target.recordId] })
    void queryClient.invalidateQueries({ queryKey: ['activities', target.type, target.recordId] })
  }
}

/**
 * Режимы композера по правам: комментарий — право на комментарии, звонок и задача —
 * правка записи. Звонок — работа сотрудника из оргструктуры: учётной записи без карточки
 * сотрудника его некому приписать, и сервер отказал бы.
 */
export function useComposerModes(target: ActivityTarget, placeholders: { comment: string }): ComposerMode[] {
  const { t, user } = useApp()
  const refresh = useRefreshActivity(target)
  const modes: ComposerMode[] = []
  // A call or a task needs the activity grant as well as the record (V63).
  const mayUpdate = can(user, target.resource, 'update') && mayLogActivity(user)
  if (can(user, 'system.comment', 'create')) {
    modes.push({
      id: 'comment',
      label: t('timeline.kind.comment'),
      icon: 'comment',
      placeholder: placeholders.comment,
      mentions: searchMentionable,
      submit: async ({ text, mentionedUserIds }) => {
        await postComment(target.entity, target.recordId, text, { mentionedUserIds })
        refresh()
      },
    })
  }
  if (mayUpdate && user?.employeeId) {
    const employeeId = user.employeeId
    modes.push({
      id: 'call',
      label: t('timeline.kind.call'),
      icon: 'phone',
      placeholder: t('record.composer.call'),
      extra: 'duration',
      submit: async ({ text, durationMinutes }) => {
        await logCall({
          entityType: target.type,
          entityId: target.recordId,
          subject: text,
          direction: 'outbound',
          durationSeconds: durationMinutes === undefined ? null : Math.round(durationMinutes * 60),
          employeeId,
        })
        refresh()
      },
    })
  }
  if (mayUpdate && user?.employeeId) {
    const employeeId = user.employeeId
    modes.push({
      id: 'task',
      label: t('timeline.kind.task'),
      icon: 'work',
      placeholder: t('record.composer.task'),
      extra: 'due',
      submit: async ({ text, due }) => {
        await createTask({
          entityType: target.type,
          entityId: target.recordId,
          subject: text,
          assigneeId: employeeId,
          dueAt: due ? new Date(due).toISOString() : null,
        })
        refresh()
      },
    })
  }
  return modes
}

/** «Следующий шаг» записи: ближайшая открытая задача или предупреждение, что её нет. */
export function NextStepBlock({
  target,
  activities,
  missingHint,
  onAssign,
}: {
  target: ActivityTarget
  activities: { isSuccess: boolean; isError: boolean; data: Activity[] | undefined }
  missingHint: string
  onAssign?: () => void
}) {
  const { t, user, locale } = useApp()
  const refresh = useRefreshActivity(target)
  const complete = useMutation({ mutationFn: (id: string) => completeActivity(id), onSuccess: refresh })
  if (activities.isError) return <StateView compact kind="error" title={t('record.deal.next.error')} />
  if (!activities.isSuccess) return null
  const step = nextStepOf(activities.data ?? [])
  const mayUpdate = can(user, target.resource, 'update')
  return (
    <NextStep
      task={step ? stepTask(step, locale, t) : null}
      actions={
        step && mayUpdate && mayCompleteActivity(user, step) ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={complete.isPending}
            onClick={() => complete.mutate(step.id)}
          >
            <Icon name="check" size="sm" />
            {t('record.next.done')}
          </button>
        ) : undefined
      }
      missingHint={missingHint}
      missingAction={
        onAssign ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onAssign}>
            {t('record.next.assign')}
          </button>
        ) : undefined
      }
    />
  )
}

/** Лента записи с честными состояниями: загрузка, нет доступа, ошибка, пусто. */
export function RecordFeed({
  timeline,
  emptyTitle,
  emptyText,
  limit,
  title,
  footer,
}: {
  timeline: TimelineFeed
  emptyTitle: string
  emptyText?: string
  /** Сколько последних событий показать; без него — все загруженные. */
  limit?: number
  title?: string
  footer?: React.ReactNode
}) {
  const { t } = useApp()
  if (timeline.isLoading) return <p className="muted">{t('app.loading')}</p>
  if (timeline.isError) {
    const forbidden = timeline.error instanceof ApiError && timeline.error.isForbidden
    return (
      <StateView
        compact
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('record.state.forbidden.title') : t('record.feed.loadError')}
      />
    )
  }
  const all: FeedItem[] = (timeline.data?.events ?? []).map((event) => feedItemOf(event, t))
  const items = limit === undefined
    ? all
    : [...all].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, limit)
  return (
    <Feed
      title={title}
      items={items}
      empty={<StateView compact kind="empty" title={emptyTitle} description={emptyText} />}
      // A preview (limit) is not where the history is paged; the full feed is (#114).
      footer={footer ?? (limit === undefined ? <TimelineMore feed={timeline} /> : undefined)}
    />
  )
}

export { Composer }

type Translate = (key: string, params?: Record<string, string | number>) => string

function stepTask(step: Activity, locale: string, t: Translate) {
  const due = step.dueAt ? new Date(step.dueAt) : null
  const when =
    due && !Number.isNaN(due.getTime())
      ? due.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('record.next.noDue')
  return { title: step.subject, details: when, overdue: step.overdue, icon: 'work' as const }
}
